/**
 * Consent dialog component.
 *
 * Collects user consent for privacy policy and terms of service.
 * Shown before creating a new account.
 */

import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface ConsentDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog closes */
  onClose: () => void;
  /** Callback when consent is given */
  onConsent: (consent: ConsentData) => void | Promise<void>;
}

export interface ConsentData {
  /** User accepted privacy policy */
  privacyAccepted: boolean;
  /** User accepted terms of service */
  termsAccepted: boolean;
  /** User consented to analytics */
  analyticsConsent: boolean;
  /** User consented to marketing */
  marketingConsent: boolean;
}

/**
 * Dialog for collecting user consent before account creation.
 */
export function ConsentDialog({
  open,
  onClose,
  onConsent,
}: ConsentDialogProps): React.ReactElement {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = privacyAccepted && termsAccepted;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;

    setSubmitting(true);

    try {
      await onConsent({
        privacyAccepted,
        termsAccepted,
        analyticsConsent,
        marketingConsent,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Welcome to Idle Depths!</DialogTitle>
          <DialogDescription>
            Before creating your account, please review and accept our policies.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Required consents */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={(checked) => {
                  setPrivacyAccepted(checked === true);
                }}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="privacy" className="text-sm font-medium">
                  Privacy Policy <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  I have read and agree to the{' '}
                  <Link to="/privacy" className="underline hover:text-foreground" target="_blank">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => {
                  setTermsAccepted(checked === true);
                }}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="terms" className="text-sm font-medium">
                  Terms of Service <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  I have read and agree to the{' '}
                  <Link to="/terms" className="underline hover:text-foreground" target="_blank">
                    Terms of Service
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Optional consents */}
          <div className="space-y-3 border-t pt-4">
            <p className="text-xs text-muted-foreground">Optional:</p>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="analytics"
                checked={analyticsConsent}
                onCheckedChange={(checked) => {
                  setAnalyticsConsent(checked === true);
                }}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="analytics" className="text-sm font-medium">
                  Analytics
                </Label>
                <p className="text-xs text-muted-foreground">
                  Help improve the game by sharing anonymous usage data
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="marketing"
                checked={marketingConsent}
                onCheckedChange={(checked) => {
                  setMarketingConsent(checked === true);
                }}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="marketing" className="text-sm font-medium">
                  Updates & News
                </Label>
                <p className="text-xs text-muted-foreground">
                  Receive emails about game updates and new features
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit || submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
